import { Component, inject, OnInit } from '@angular/core';
import { CardComponent } from "../../../../shared/components/card/card.component";
import { IconComponent } from "../../../../shared/icons/icon.component";
import { TranslatePipe } from '@ngx-translate/core';
import {MatDialog} from '@angular/material/dialog';
import {DialogDemoComponent} from '../../components/dialog-demo/dialog-demo.component';
import {DialogInviteGroupComponent} from '../../components/dialog-invite-group/dialog-invite-group.component';
import { HistoryComponent } from "../../../devices/components/history/history.component";
import {AsyncPipe} from '@angular/common';
import {Store} from '@ngrx/store';
import {ScenarioActions} from '../../state/scenario.actions';
import {
  selectIsScenariosLoading,
  selectScenarioHistory,
  selectRunningScenario
} from '../../state/scenario.selectors';
import {RunningScenario} from '../../state/scenario.state';
import {combineLatest, map, Observable, of, switchMap, timer} from 'rxjs';
import {HistoryDataViewModel} from '../../../devices/models/history-view-model.interface';
import {ApiScenarioHistoryEntryResponseDto} from '@birco/rainmesh-api';
import {IdleTimeoutService} from '../../../../core/services/idle-timeout.service';
import {ShowcaseSessionService} from '../../../../core/services/showcase-session.service';
import {ErrorToastService} from '../../../../core/services/error-toast.service';
import { LogoComponent } from "../../../../shared/components/logo/logo.component";
import {UserService} from '@sso/ld-sso-with-oauth2-oidc';
import {Role} from '../../../../core/enums/role.enum';
import {findDemoOptionCopyById, ShowcaseUseCase} from '../../config/demo-options';
import {ScenarioAnimationComponent} from '../../../../shared/components/scenario-animation/scenario-animation.component';
import {ProcessingVisualComponent} from '../../components/processing-visual/processing-visual.component';

export interface RunningScenarioViewModel {
  displayName: string;
  headline?: string;
  description?: string;
  useCase?: ShowcaseUseCase;
  percent: number;
  elapsedDisplay: string;
  totalDisplay: string;
}

// startedAt/stoppedAt/endedAt arrive as ISO-8601 UTC with microseconds (6 fractional
// digits, e.g. "2026-08-19T10:15:30.000000Z") - more precision than the ECMA-262
// date-time string format guarantees every engine parses, so truncate to milliseconds
// before handing it to `Date`.
function parseIsoTimestamp(iso: string): number {
  return new Date(iso.replace(/(\.\d{3})\d*(Z)$/, '$1$2')).getTime();
}

@Component({
  selector: 'app-page-demo',
  imports: [CardComponent, IconComponent, TranslatePipe, HistoryComponent, AsyncPipe, LogoComponent, ScenarioAnimationComponent, ProcessingVisualComponent],
  templateUrl: './page-demo.component.html',
  styleUrl: './page-demo.component.scss'
})
export class PageDemoComponent implements OnInit {
  private dialog = inject(MatDialog);
  private store = inject(Store);
  private idleTimeoutService = inject(IdleTimeoutService);
  private showcaseSessionService = inject(ShowcaseSessionService);
  private errorToastService = inject(ErrorToastService);
  private userService = inject(UserService);

  remainingIdleSeconds$ = this.idleTimeoutService.remainingSeconds$;

  // Only the holder (RM_DEMO) may start scenarios or invite companions - a group
  // member (RM_DEMO_GROUP) joined via an invite link has read-only access and
  // would be rejected by the backend for either action.
  isHolder$ = this.userService.getUserInfo$().pipe(
    map((user) => (user?.info?.roles ?? []).includes(Role.RM_DEMO)),
  );

  // Only relevant for group members (see the `!isHolder` template branch) - the scenario
  // currently running, pushed over the /ws/v2/demo feed and kept in ScenarioState.
  runningScenario$: Observable<RunningScenarioViewModel | null> = this.store
    .select(selectRunningScenario)
    .pipe(
      switchMap((running) =>
        running ? this.buildRunningScenarioViewModel$(running) : of(null),
      ),
    );

  history$: Observable<HistoryDataViewModel> = combineLatest([
    this.store.select(selectScenarioHistory),
    this.store.select(selectIsScenariosLoading),
  ]).pipe(
    // Global scenario history (API is still id-bound today; effect adapts internally until new endpoint arrives).
    map(([historyResponseDto, loading]) => ({
      data: this.groupByDate(historyResponseDto ?? []),
      loading,
      canLoadMore: false,
    })),
  );

  ngOnInit() {
    this.store.dispatch(ScenarioActions.public.loadAllScenarios());
  }

  startDemo() {
    this.dialog.open(DialogDemoComponent, {
      maxWidth: '100vw',
      panelClass: 'demo-dialog',
      disableClose: true,
    });
  }

  inviteGroup() {
    this.showcaseSessionService.generateGroupInvitation().subscribe((result) => {
      if (result.status === 'created') {
        this.dialog.open(DialogInviteGroupComponent, {
          data: { link: result.link },
        });
      } else {
        this.errorToastService.show();
      }
    });
  }

  formatIdleCountdown(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  private buildRunningScenarioViewModel$(
    running: RunningScenario,
  ): Observable<RunningScenarioViewModel> {
    return timer(0, 1000).pipe(
      map(() => {
        const copy = findDemoOptionCopyById(running.scenarioId);
        const totalSeconds = running.runtimeMinutes * 60;
        const elapsedSeconds = Math.max(
          0,
          Math.floor((Date.now() - parseIsoTimestamp(running.startedAt)) / 1000),
        );

        return {
          displayName: running.displayName,
          headline: copy?.headline,
          description: copy?.description,
          useCase: copy?.useCase,
          percent: Math.min(100, (elapsedSeconds / totalSeconds) * 100),
          elapsedDisplay: this.formatIdleCountdown(elapsedSeconds),
          totalDisplay: this.formatIdleCountdown(totalSeconds),
        };
      }),
    );
  }

  private groupByDate(items: ApiScenarioHistoryEntryResponseDto[]) {
    return Array.from(
      items.reduce((groups, item) => {
        const dateKey = new Date(item.eventDate).toDateString();
        if (!groups.has(dateKey)) {
          groups.set(dateKey, []);
        }
        groups.get(dateKey)!.push(item);
        return groups;
      }, new Map<string, ApiScenarioHistoryEntryResponseDto[]>()),
      ([date, groupedItems]) => ({
        date,
        items: groupedItems as any,
      }),
    );
  }
}
